import { useMemo, useSyncExternalStore } from 'react';
import { useAccount, useReadContracts, useBalance } from 'wagmi';
import { scroogeAbi, SCROOGE_ADDRESS, IS_DEPLOYED } from '../../lib/contract';
import { USDT_ADDRESS, erc20Abi } from '../../lib/addresses';
import { mockStore } from '../../lib/mockStore';
import { planDurationSec, computeAccrued } from '../../lib/format';
import { FALLBACK_PLANS } from './useAllPlans';

// Single batched read: getUserInfo + getDeposits + pendingProfitAll + USDT balance + allowance.
// staleTime: 15s, refetch on explicit invalidate после успешной tx.
// Demo-режим (IS_DEPLOYED=false) читает mockStore вместо цепочки.
export function useUserPortfolio() {
  const { address, isConnected } = useAccount();
  const enabledReal = Boolean(address) && isConnected && IS_DEPLOYED;

  const contracts = useMemo(
    () =>
      address
        ? [
            { address: SCROOGE_ADDRESS, abi: scroogeAbi, functionName: 'getUserInfo', args: [address] },
            { address: SCROOGE_ADDRESS, abi: scroogeAbi, functionName: 'getDeposits', args: [address] },
            { address: SCROOGE_ADDRESS, abi: scroogeAbi, functionName: 'pendingProfitAll', args: [address] },
            { address: USDT_ADDRESS, abi: erc20Abi, functionName: 'balanceOf', args: [address] },
            { address: USDT_ADDRESS, abi: erc20Abi, functionName: 'allowance', args: [address, SCROOGE_ADDRESS] },
          ]
        : [],
    [address],
  );

  const query = useReadContracts({
    contracts,
    query: { enabled: enabledReal, staleTime: 15_000, refetchInterval: 30_000 },
  });

  // Mock snapshot реактивно подписывается на mockStore — обновляется после каждой mutation.
  const mockSnap = useSyncExternalStore(
    mockStore.subscribe,
    () => mockStore.getSnapshot(address || 'anon'),
    () => mockStore.getSnapshot(address || 'anon'),
  );

  if (!IS_DEPLOYED) {
    // Расчёт pendingAll по mock-deposits с учётом fallback-планов
    const nowSec = Math.floor(Date.now() / 1000);
    let pendingAll = 0n;
    for (const d of mockSnap.deposits) {
      if (!d.active) continue;
      const plan = FALLBACK_PLANS[Number(d.planId)];
      if (!plan) continue;
      pendingAll += computeAccrued({
        amount: d.amount,
        dailyBps: plan.dailyBps,
        startedAt: d.startedAt,
        lastClaimAt: d.lastClaimAt,
        durationSec: planDurationSec(plan),
        now: nowSec,
      });
    }
    return {
      isMock: true,
      isReady: Boolean(address) && isConnected,
      isLoading: false,
      userInfo: mockSnap.userInfo,
      deposits: mockSnap.deposits,
      pendingAll,
      usdtBalance: mockSnap.usdtBalance,
      allowance: mockSnap.allowance,
      address,
      refetch: () => {},
    };
  }

  const [userInfo, deposits, pendingAll, usdtBalance, allowance] = query.data ?? [];

  return {
    ...query,
    isMock: false,
    isReady: enabledReal,
    userInfo: userInfo?.result,
    deposits: deposits?.result ?? [],
    pendingAll: pendingAll?.result ?? 0n,
    usdtBalance: usdtBalance?.result ?? 0n,
    allowance: allowance?.result ?? 0n,
    address,
  };
}

// Отдельный хук для USDT balance — нужен в UI когда основной portfolio ещё не готов
export function useUsdtBalance() {
  const { address } = useAccount();
  return useBalance({
    address,
    token: USDT_ADDRESS,
    query: { enabled: Boolean(address) && IS_DEPLOYED, staleTime: 10_000 },
  });
}
